import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { WarningIcon as Warning, CheckCircleIcon as CheckCircle, ArrowSquareOutIcon as ArrowSquareOut, BugBeetleIcon as BugBeetle } from "@phosphor-icons/react";

/**
 * SentryHealthTile — at-a-glance Sentry status on the admin Ops Hub.
 *
 * Surfaces open-issue count, latest event timestamp, and a deep link
 * to the org+project Issues page. Auto-hides when Sentry isn't
 * configured (preview, dev). Polls once on mount; no live refresh —
 * the deep link is one click away.
 */
export default function SentryHealthTile() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/sentry/health", { silent: true })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data || data.configured === false) return null;

  const fmtRelative = (iso) => {
    if (!iso) return "never";
    try {
      const t = new Date(iso);
      const diffMs = Date.now() - t.getTime();
      const mins = Math.floor(diffMs / 60_000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      return `${days}d ago`;
    } catch { return iso; }
  };

  // Upstream failed — render an "ok=false" strip rather than hiding,
  // so admin notices Sentry itself is misconfigured (token expired,
  // org/project slug typo, etc.).
  if (data.ok === false) {
    return (
      <section className="mt-10" data-testid="sentry-health-tile-error">
        <div className="rounded-2xl border-2 border-crimson/40 bg-[#FFF0F0] p-4 flex items-start gap-3">
          <Warning size={20} weight="fill" className="text-crimson mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="font-spectral text-base">Sentry: upstream check failed</h3>
            <p className="text-[12px] text-[#3F3F3F] mt-0.5">
              {data.upstream_status ? `Sentry returned HTTP ${data.upstream_status}.` : (data.error || "Unknown error.")}
              {" "}Verify <code className="text-[11px]">SENTRY_AUTH_TOKEN</code> + <code className="text-[11px]">SENTRY_ORG</code> + <code className="text-[11px]">SENTRY_PROJECT</code>.
            </p>
            {data.deep_link && (
              <a
                href={data.deep_link}
                target="_blank"
                rel="noreferrer"
                className="text-[12px] font-bold text-ink hover:text-crimson underline inline-flex items-center gap-1 mt-1"
              >
                Open Sentry <ArrowSquareOut size={11} weight="bold" />
              </a>
            )}
          </div>
        </div>
      </section>
    );
  }

  const count = data.open_issue_count || 0;
  const isQuiet = count === 0;
  const tone = isQuiet
    ? { bg: "bg-[#EEFBEC]", border: "border-[#BFE8C9]", icon: <CheckCircle size={20} weight="fill" className="text-[#0F8043]" /> }
    : count <= 3
    ? { bg: "bg-[#FBF7EC]", border: "border-[#E8D9A6]", icon: <BugBeetle size={20} weight="duotone" className="text-gold-dark" /> }
    : { bg: "bg-[#FFF0F0]", border: "border-crimson/40", icon: <Warning size={20} weight="fill" className="text-crimson" /> };

  return (
    <section className="mt-10" data-testid="sentry-health-tile">
      <div className={`rounded-2xl border-2 ${tone.border} ${tone.bg} p-5`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <span className="p-2 rounded-full bg-white shrink-0">{tone.icon}</span>
            <div className="min-w-0">
              <h2 className="font-spectral text-xl tracking-tight">
                Sentry — {isQuiet ? "all clear" : `${count} open issue${count === 1 ? "" : "s"}`}
              </h2>
              <p className="text-[12.5px] text-[#3F3F3F] mt-0.5">
                {isQuiet
                  ? "No unresolved errors in the current project."
                  : <>Last event {fmtRelative(data.last_seen)}.</>}
              </p>
            </div>
          </div>
          <a
            href={data.deep_link}
            target="_blank"
            rel="noreferrer"
            data-testid="sentry-health-deep-link"
            className="inline-flex items-center gap-1 text-[12px] font-bold text-ink hover:text-crimson rounded-full border border-ink/30 px-3 py-1.5 bg-white whitespace-nowrap"
          >
            Open Sentry <ArrowSquareOut size={11} weight="bold" />
          </a>
        </div>

        {data.top_issues && data.top_issues.length > 0 && (
          <ul className="mt-4 space-y-1.5" data-testid="sentry-health-top-issues">
            {data.top_issues.slice(0, 5).map((iss) => (
              <li key={iss.id} className="flex items-center justify-between gap-2 text-sm bg-white/70 rounded-lg p-2.5 border border-white">
                <div className="min-w-0 flex-1">
                  <a
                    href={iss.permalink}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-ink hover:text-crimson truncate block"
                    title={iss.title}
                  >
                    {iss.title}
                  </a>
                  <p className="text-[11px] text-[#6B6B6B]">
                    {iss.count ? `${iss.count} event${iss.count === 1 ? "" : "s"}` : ""}
                    {iss.user_count ? ` · ${iss.user_count} user${iss.user_count === 1 ? "" : "s"}` : ""}
                    {iss.last_seen ? ` · ${fmtRelative(iss.last_seen)}` : ""}
                  </p>
                </div>
                {iss.level && (
                  <span className={`text-[10px] uppercase tracking-wider font-bold whitespace-nowrap px-2 py-0.5 rounded ${
                    iss.level === "fatal" || iss.level === "error" ? "bg-crimson/10 text-crimson"
                      : iss.level === "warning" ? "bg-[#FBF7EC] text-gold-dark"
                      : "bg-ink/5 text-ink"
                  }`}>
                    {iss.level}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
